import { useState, useEffect, useRef } from 'react';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const LOAN_TYPES = [
  { value: 'member_loan',          label: 'Standard Member Loan' },
  { value: 'emergency_loan',       label: 'Emergency Loan' },
  { value: 'development_loan',     label: 'Development Loan' },
  { value: 'rosca_advance',        label: 'ROSCA Advance' },
  { value: 'security_backed_loan', label: 'Security-Backed Loan' },
];

const fmtTS = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' EAT';
};

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// ── Payment Advice urgency helpers ────────────────────────────
// Lets a member visually compare "loan due in 3 days" against
// "savings due in 18 days" at a glance, rather than everything on
// the slip looking equally urgent.
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}
function urgencyInfo(days, noDueDate) {
  // MIFL Arrears specifically calls this with a dedicated flag —
  // arrears exist BECAUSE something already passed its due date and
  // was escalated, so there is no "no fixed due date, neutral"
  // reading for them; they must always present as overdue (or, when
  // the balance is genuinely zero, a clean CLEAR) rather than an
  // absence of information.
  if (noDueDate === 'arrears') return { label: 'OVERDUE — MIFL ARREARS', color: '#e74c3c' };
  if (noDueDate === 'arrears-clear') return { label: 'CLEAR', color: '#2ecc71' };
  if (noDueDate || days === null) return { label: 'No fixed due date', color: '#7f8c8d' };
  if (days < 0) return { label: `${Math.abs(days)}d overdue`, color: '#e74c3c' };
  if (days === 0) return { label: 'Due today', color: '#e74c3c' };
  if (days <= 5) return { label: `${days}d left — urgent`, color: '#e67e22' };
  if (days <= 14) return { label: `${days}d left`, color: '#b7950b' };
  return { label: `${days}d left`, color: '#27ae60' };
}

function MemberPortal() {
  const [tab, setTab] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [mifl, setMifl] = useState([]);
  const [miflSummary, setMiflSummary] = useState(null);
  const [loans, setLoans] = useState([]);
  const [fines, setFines] = useState([]);
  const [rosca, setRosca] = useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [savings, setSavings] = useState([]);
  const [arrears, setArrears] = useState([]);
  const [financialPos, setFinancialPos] = useState(null);
  const [treasuryAccounts, setTreasuryAccounts] = useState([]);
  const [projections, setProjections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [bioPData, setBioPData]         = useState(null);
  const [bioChildrenP, setBioChildrenP] = useState([]);

  const [advice, setAdvice] = useState(null);
  const [customSlipLines, setCustomSlipLines] = useState([]);
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [declarations, setDeclarations] = useState([]);
  const [declForm, setDeclForm] = useState({ amount: '', notes: '' });
  const [declFile, setDeclFile] = useState(null);
  const [declSubmitting, setDeclSubmitting] = useState(false);
  const [declMsg, setDeclMsg] = useState('');
  const [declMsgType, setDeclMsgType] = useState('success');
  const fileInputRef = useRef(null);

  const [loanApps, setLoanApps] = useState([]);
  const [loanForm, setLoanForm] = useState({ loan_type: 'member_loan', amount_requested: '', purpose: '' });
  const [loanSubmitting, setLoanSubmitting] = useState(false);
  const [loanMsg, setLoanMsg] = useState('');
  const [loanMsgType, setLoanMsgType] = useState('success');

  const [agreementLoading, setAgreementLoading]   = useState(null);
  const [receiptLoading, setReceiptLoading]       = useState(null);
  const [loanActionMsg, setLoanActionMsg]         = useState('');
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, m, ms, l, f, r, s, sv, a] = await Promise.all([
        api.get('/api/portal/profile'),
        api.get('/api/portal/mifl'),
        api.get('/api/portal/mifl-summary'),
        api.get('/api/portal/loans'),
        api.get('/api/portal/fines'),
        api.get('/api/portal/rosca'),
        api.get('/api/portal/subscriptions'),
        api.get('/api/portal/savings'),
        api.get('/api/portal/arrears-list'),
      ]);
      setProfile(p.data);
      setMifl(m.data);
      setMiflSummary(ms.data);
      setLoans(l.data);
      setFines(f.data);
      setRosca(r.data);
      setSubscriptions(s.data);
      setSavings(sv.data);
      setArrears(a.data);

      try {
        const bioRes = await api.get(`/api/member-bio/${p.data.id}`);
        setBioPData(bioRes.data.bio);
        setBioChildrenP(bioRes.data.children || []);
      } catch (_) {}

      const [fp, ta, proj] = await Promise.all([
        api.get('/api/treasury/financial-position'),
        api.get('/api/treasury/accounts'),
        api.get('/api/projections'),
      ]);
      setFinancialPos(fp.data);
      setTreasuryAccounts(ta.data);
      if (proj.data.length > 0) {
        const detail = await api.get(`/api/projections/${proj.data[0].id}`);
        setProjections(detail.data);
      }
    } catch (err) {
      setError('Failed to load your data');
    }
    setLoading(false);
  };

  const refreshLoans = async () => {
    try {
      const l = await api.get('/api/portal/loans');
      setLoans(l.data);
    } catch (_) {}
  };

  const loadPaymentsTab = async () => {
    setAdviceLoading(true);
    try {
      const [adv, decl, apps, customRes] = await Promise.all([
        api.get('/api/portal/payment-advice'),
        api.get('/api/portal/payment-declarations'),
        api.get('/api/portal/loan-applications'),
        api.get(`/api/payments/custom-lines/me`).catch(() => ({ data: [] })),
      ]);
      setAdvice(adv.data);
      setDeclarations(decl.data);
      setLoanApps(apps.data);
      setCustomSlipLines(customRes.data || []);
    } catch (err) {
      console.error('payments tab load error:', err.message);
    }
    setAdviceLoading(false);
  };

  const handleTabChange = (t) => {
    setTab(t);
    if ((t === 'payments' || t === 'loan_apply') && !advice) loadPaymentsTab();
  };

  const acceptAgreement = async (loanId) => {
    setAgreementLoading(loanId);
    setLoanActionMsg('');
    try {
      await api.post(`/api/loans/${loanId}/accept-agreement`);
      setLoanActionMsg('✅ Agreement accepted — your loan agreement is now binding.');
      setShowAcceptConfirm(null);
      await refreshLoans();
    } catch (err) {
      setLoanActionMsg('Error: ' + (err.response?.data?.error || 'Failed to accept agreement'));
    }
    setAgreementLoading(null);
  };

  const confirmReceipt = async (loanId) => {
    setReceiptLoading(loanId);
    setLoanActionMsg('');
    try {
      await api.post(`/api/loans/${loanId}/confirm-receipt`);
      setLoanActionMsg('✅ Receipt confirmed — you have acknowledged receiving the disbursed funds.');
      await refreshLoans();
    } catch (err) {
      setLoanActionMsg('Error: ' + (err.response?.data?.error || 'Failed to confirm receipt'));
    }
    setReceiptLoading(null);
  };

  const generateAdvicePDF = () => {
    if (!advice) return;
    const doc = new jsPDF();
    const now = new Date();

    doc.setFillColor(44, 62, 80);
    doc.rect(0, 0, 210, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('Kira Municipality, Uganda | Registered CBO', 105, 20, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text('MEMBER PAYMENT ADVICE SLIP', 105, 40, { align: 'center' });

    doc.setFillColor(240, 240, 240);
    doc.rect(15, 46, 180, 30, 'F');
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
    doc.text('MEMBER DETAILS', 18, 52);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
    doc.text(`Name: ${advice.member?.full_name || profile?.full_name}`, 18, 58);
    doc.text(`Member No: ${advice.member?.member_number || profile?.member_number}`, 18, 63);
    doc.text(`Period: ${MONTHS[advice.period.month - 1]} ${advice.period.year}`, 130, 58);
    doc.text(`Generated: ${now.toLocaleDateString('en-GB')}`, 130, 63);
    if (advice.window) {
      doc.setFontSize(8); doc.setTextColor(90, 90, 90);
      doc.text(`Valid: ${advice.window.start} to ${advice.window.end}`, 18, 74);
      doc.setFontSize(9); doc.setTextColor(0, 0, 0);
    }
    const memberNumber = advice.member?.member_number || profile?.member_number;
    const memberUsername = profile?.username || `KAMS${String(memberNumber || '').padStart(3, '0')}`;
    doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
    doc.text(`Payment Reference: ${memberUsername} or ${String(memberNumber || '').padStart(3, '0')}`, 18, 69);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);

    const tableRows = [];
    if (advice.mifl_arrears.amount > 0)
      tableRows.push([advice.mifl_arrears.label, advice.mifl_arrears.amount.toLocaleString(), 'Past missed obligations']);
    if (advice.obligations && advice.obligations.length > 0) {
      advice.obligations.forEach(o => {
        tableRows.push([o.label || `${o.obligation_type} — Month ${o.month_number}`, Number(o.amount).toLocaleString(), `Status: ${o.status} | Due: ${o.due_date?.slice(0,10) || ''}`]);
      });
    }
    if (advice.subscription.amount > 0)
      tableRows.push([advice.subscription.label, advice.subscription.amount.toLocaleString(), `Status: ${advice.subscription.status}`]);
    // Fines are no longer summarised here — each individual fine now
    // arrives as its own row via the advice.obligations loop above,
    // complete with its own due date. Adding a combined summary row
    // here on top of that would double-count the same fines.

    if (customSlipLines.length > 0) {
      customSlipLines.forEach(l => {
        const note = [l.due_date ? `Due: ${l.due_date.slice(0,10)}` : '', l.notes || ''].filter(Boolean).join(' | ');
        tableRows.push([l.description, parseFloat(l.amount).toLocaleString(), note || '—']);
      });
    }

    if (tableRows.length === 0)
      tableRows.push(['No outstanding obligations', '0', 'All clear']);

    const customTotal = customSlipLines.reduce((s,l) => s + parseFloat(l.amount), 0);
    const grandTotal = advice.total_to_deposit + customTotal;

    autoTable(doc, {
      startY: 82,
      head: [['Obligation', 'Amount (UGX)', 'Note']],
      body: tableRows,
      foot: [['TOTAL TO DEPOSIT', `UGX ${grandTotal.toLocaleString()}`, '']],
      headStyles: { fillColor: [44, 62, 80] },
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold', fontSize: 12 },
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 15, right: 15 },
    });

    const finalY = doc.lastAutoTable.finalY + 14;
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
    doc.text('BANKING DETAILS', 15, finalY);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
    doc.text('Centenary Bank — Account Name: Kigezi Association', 15, finalY + 7);
    doc.text('Account Number: 3100125358', 15, finalY + 13);
    doc.text('DFCU Bank — Account Name: Kigezi Association', 15, finalY + 20);
    doc.text('Account Number: 01521117543559', 15, finalY + 26);

    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(80, 80, 80);
    doc.text('Please deposit the total amount above to either account and upload your deposit slip via the member portal.', 15, finalY + 36, { maxWidth: 180 });
    doc.text('Finance will confirm your payment by the following Monday.', 15, finalY + 44, { maxWidth: 180 });

    const ph = doc.internal.pageSize.height;
    doc.setFillColor(44, 62, 80);
    doc.rect(0, ph - 15, 210, 15, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text('Kigezi Association — Official Document — Confidential', 105, ph - 6, { align: 'center' });

    doc.save(`PaymentAdvice-${advice.member?.member_number || ''}-${MONTHS[advice.period.month - 1]}${advice.period.year}.pdf`);
  };

  const submitDeclaration = async () => {
    if (!declForm.amount || parseFloat(declForm.amount) <= 0)
      return showDeclMsg('Enter a valid amount', 'error');
    setDeclSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('amount', declForm.amount);
      fd.append('notes', declForm.notes);
      if (declFile) fd.append('deposit_slip', declFile);
      await api.post('/api/portal/payment-declaration', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showDeclMsg('Declaration submitted — pending Finance confirmation', 'success');
      setDeclForm({ amount: '', notes: '' });
      setDeclFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      const decl = await api.get('/api/portal/payment-declarations');
      setDeclarations(decl.data);
    } catch (err) {
      showDeclMsg(err.response?.data?.error || 'Submission failed', 'error');
    }
    setDeclSubmitting(false);
  };

  const submitLoanApplication = async () => {
    if (!loanForm.amount_requested || parseFloat(loanForm.amount_requested) <= 0)
      return showLoanMsg('Enter a valid amount', 'error');
    if (!loanForm.purpose.trim())
      return showLoanMsg('Purpose / reason is required', 'error');
    setLoanSubmitting(true);
    try {
      await api.post('/api/portal/loan-application', loanForm);
      showLoanMsg('Loan application submitted — Finance will review and respond', 'success');
      setLoanForm({ loan_type: 'member_loan', amount_requested: '', purpose: '' });
      const apps = await api.get('/api/portal/loan-applications');
      setLoanApps(apps.data);
    } catch (err) {
      showLoanMsg(err.response?.data?.error || 'Submission failed', 'error');
    }
    setLoanSubmitting(false);
  };

  const showDeclMsg = (text, type) => { setDeclMsg(text); setDeclMsgType(type); setTimeout(() => setDeclMsg(''), 5000); };
  const showLoanMsg = (text, type) => { setLoanMsg(text); setLoanMsgType(type); setTimeout(() => setLoanMsg(''), 5000); };
  const prefillAdviceAmount = () => { if (advice) setDeclForm(f => ({ ...f, amount: String(advice.total_to_deposit) })); };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#95a5a6' }}>Loading your portal...</div>;
  if (error)   return <div style={{ padding: '40px', textAlign: 'center', color: '#e74c3c' }}>{error}</div>;

  const miflBalance  = mifl.length ? parseFloat(mifl[mifl.length - 1].balance) : 0;
  const netDebits    = miflSummary ? miflSummary.net_debits : Math.max(0, miflBalance);
  const netCredit    = miflSummary ? miflSummary.net_credit : 0;
  const activeLoan   = loans.find(l => l.status === 'active');
  const unpaidFines  = fines.filter(f => f.status !== 'paid');
  const totalFines   = unpaidFines.reduce((s, f) => s + parseFloat(f.balance), 0);
  const activeReg    = rosca.registrations?.find(r => r.cycle_status === 'active');
  const mySlot       = rosca.slots?.[0];
  const statusColor  = (s) => s === 'active' || s === 'good_standing' || s === 'in_credit' ? '#2ecc71' : s === 'in_arrears' ? '#e74c3c' : '#95a5a6';
  const statusLabel  = (s) => {
    if (s === 'in_credit' || s === 'active') return 'GOOD STANDING';
    if (s === 'good_standing') return 'GOOD STANDING';
    if (s === 'in_arrears') return 'IN ARREARS';
    return s?.replace('_', ' ').toUpperCase() || 'ACTIVE';
  };

  const TABS = [
    { key: 'overview',      label: '🏠 Overview' },
    { key: 'mifl',          label: '📋 MIFL Statement' },
    { key: 'loans',         label: '💰 Loans' },
    { key: 'fines',         label: '⚠ Fines' },
    { key: 'rosca',         label: '🔄 ROSCA' },
    { key: 'subscriptions', label: '📅 Subscriptions' },
    { key: 'savings',       label: '🏦 Savings' },
    { key: 'payments',      label: '🏧 Payments' },
    { key: 'loan_apply',    label: '📝 Apply for Loan' },
    { key: 'finance',       label: '📊 Finance Dashboard' },
    { key: 'treasury',      label: '🏛 Treasury' },
    { key: 'projections',   label: '📈 Projections' },
    { key: 'arrears',       label: '🔴 Arrears List' },
    { key: 'bio',           label: '👤 My BIO' },
  ];

  return (
    <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>

      {profile && (
        <div style={{ background: '#2c3e50', color: 'white', borderRadius: '10px', padding: '20px 24px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px' }}>{getGreeting()}, {profile.full_name.split(' ')[1] || profile.full_name}</h2>
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#f39c12', fontWeight: '500' }}>Welcome to Kigezi Association's digital home — real-time net worth, audited transparency, and a permanent ledger of trust.</p>
            <p style={{ margin: '6px 0 0', opacity: 0.7, fontSize: '12px' }}>{profile.full_name} — Member {profile.member_number} | Joined {profile.date_joined?.slice(0, 10)}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', opacity: 0.7 }}>Financial Status</div>
            <span style={{ background: statusColor(profile.financial_status), color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' }}>
              {statusLabel(profile.financial_status)}
            </span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {[
          { label: 'MIFL Balance', val: `UGX ${netDebits.toLocaleString()}`, sub: netDebits > 0 ? 'IN ARREARS' : 'CLEAR', color: netDebits > 0 ? '#e74c3c' : '#2ecc71' },
          ...(netCredit > 0 ? [{ label: 'Unutilised Credit', val: `UGX ${netCredit.toLocaleString()}`, sub: 'available with the Association', color: '#3498db' }] : []),
          { label: 'Active Loan',  val: activeLoan ? `UGX ${parseFloat(activeLoan.balance).toLocaleString()}` : 'None', sub: activeLoan ? 'outstanding' : '', color: activeLoan ? '#e67e22' : '#2ecc71' },
          { label: 'Unpaid Fines', val: `UGX ${totalFines.toLocaleString()}`, sub: `${unpaidFines.length} fine(s)`, color: totalFines > 0 ? '#e74c3c' : '#2ecc71' },
          { label: 'ROSCA Position', val: mySlot ? `Slot ${mySlot.slot_number}` : activeReg ? 'Registered' : 'Not registered', sub: activeReg ? activeReg.cycle_name : '', color: '#8e44ad' },
        ].map((c, i) => (
          <div key={i} style={{ background: 'white', border: `2px solid ${c.color}`, borderRadius: '8px', padding: '14px 18px', flex: '1', minWidth: '160px' }}>
            <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '4px' }}>{c.label}</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: c.color }}>{c.val}</div>
            {c.sub && <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '2px' }}>{c.sub}</div>}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #ecf0f1', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => handleTabChange(t.key)} style={{
            padding: '8px 12px', border: 'none', cursor: 'pointer', fontSize: '12px',
            background: tab === t.key ? '#2c3e50' : '#ecf0f1',
            color: tab === t.key ? 'white' : '#2c3e50',
            borderRadius: '4px 4px 0 0',
            fontWeight: t.key === 'payments' || t.key === 'loan_apply' || t.key === 'bio' ? '700' : '400'
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && profile && (
        <div style={cardStyle}>
          <h3 style={sectionTitle}>My Profile</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
            {[
              ['Full Name', profile.full_name], ['Member Number', profile.member_number],
              ['Phone', profile.phone || '—'], ['Email', profile.email || '—'],
              ['District', profile.district || '—'], ['Location', profile.location || '—'],
              ['Date Joined', profile.date_joined?.slice(0, 10) || '—'], ['Status', profile.status],
            ].map(([label, val]) => (
              <div key={label} style={{ background: '#f9f9f9', padding: '10px 14px', borderRadius: '6px' }}>
                <div style={{ fontSize: '11px', color: '#7f8c8d' }}>{label}</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2c3e50', marginTop: '2px' }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'mifl' && (
        <div style={cardStyle}>
          <h3 style={sectionTitle}>MIFL Statement</h3>
          {miflSummary && (miflSummary.net_debits > 0 || miflSummary.net_credit > 0) && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <div style={{ background: miflSummary.net_debits > 0 ? '#fde8e8' : '#eafaf1', border: `1px solid ${miflSummary.net_debits > 0 ? '#e74c3c' : '#2ecc71'}`, borderRadius: '6px', padding: '10px 16px', fontSize: '13px' }}>
                Outstanding Obligations: <strong style={{ color: miflSummary.net_debits > 0 ? '#e74c3c' : '#1e8449' }}>UGX {miflSummary.net_debits.toLocaleString()}</strong>
              </div>
              {miflSummary.net_credit > 0 && (
                <div style={{ background: '#eaf4fb', border: '1px solid #3498db', borderRadius: '6px', padding: '10px 16px', fontSize: '13px' }}>
                  Unutilised Credit: <strong style={{ color: '#3498db' }}>UGX {miflSummary.net_credit.toLocaleString()}</strong>
                </div>
              )}
            </div>
          )}
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#2c3e50', color: 'white' }}>
                <th style={thStyle}>Date</th><th style={thStyle}>Document</th><th style={thStyle}>Description</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Debit</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Credit</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {mifl.length === 0 && <tr><td colSpan="6" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No entries</td></tr>}
              {mifl.map((e, i) => (
                <tr key={e.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                  <td style={tdStyle}>{e.entry_date?.slice(0, 10)}</td>
                  <td style={tdStyle}><span style={{ background: '#2c3e50', color: 'white', padding: '2px 6px', borderRadius: '3px', fontSize: '11px' }}>{e.document_type}</span></td>
                  <td style={tdStyle}>{e.description}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#e74c3c' }}>{e.debit > 0 ? parseFloat(e.debit).toLocaleString() : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#2ecc71' }}>{e.credit > 0 ? parseFloat(e.credit).toLocaleString() : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: parseFloat(e.balance) > 0 ? '#e74c3c' : parseFloat(e.balance) < 0 ? '#3498db' : '#2ecc71' }}>
                    {parseFloat(e.balance).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'loans' && (
        <div style={cardStyle}>
          <h3 style={sectionTitle}>My Loans</h3>
          {loanActionMsg && (
            <div style={{ padding: '10px 14px', background: loanActionMsg.startsWith('Error') ? '#fde8e8' : '#d5f5e3', borderLeft: `4px solid ${loanActionMsg.startsWith('Error') ? '#e74c3c' : '#27ae60'}`, borderRadius: '4px', marginBottom: '14px', fontSize: '13px', color: loanActionMsg.startsWith('Error') ? '#c0392b' : '#1e8449' }}>
              {loanActionMsg}
            </div>
          )}
          {loans.length === 0 && <p style={{ color: '#95a5a6', textAlign: 'center' }}>No loans</p>}
          {loans.map(l => (
            <div key={l.id} style={{ border: '1px solid #ecf0f1', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <strong style={{ color: '#2c3e50' }}>{l.loan_number}</strong>
                  <span style={{ marginLeft: '10px', background: l.status === 'active' ? '#2ecc71' : '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{l.status}</span>
                </div>
                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Disbursed: {l.disbursement_date?.slice(0, 10)} | Due: {l.due_date?.slice(0, 10)}</div>
              </div>

              <div style={{ display: 'flex', gap: '20px', marginTop: '12px', flexWrap: 'wrap' }}>
                {[['Principal', l.principal], ['Balance', l.balance], ['Interest Balance', l.interest_balance], ['Monthly Instalment', l.monthly_instalment]].map(([label, val]) => (
                  <div key={label}>
                    <div style={{ fontSize: '11px', color: '#7f8c8d' }}>{label}</div>
                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#2c3e50' }}>UGX {parseFloat(val || 0).toLocaleString()}</div>
                  </div>
                ))}
              </div>

              {l.live_interest && !l.live_interest.is_term_complete && (
                <div style={{ marginTop: '14px', background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '8px', padding: '14px 16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1a5276', marginBottom: '10px' }}>
                    💰 Live Balance — What You'd Owe Today
                  </div>
                  <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#7f8c8d' }}>Principal Remaining</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1a5276' }}>UGX {l.live_interest.principal_remaining.toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#7f8c8d' }}>Interest Owed (incl. today)</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#b7770d' }}>UGX {Math.round(l.live_interest.interest_owed_now).toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#7f8c8d' }}>Total If Paid Today</div>
                      <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#c0392b' }}>UGX {Math.round(l.live_interest.total_owed_now).toLocaleString()}</div>
                    </div>
                  </div>
                  {l.live_interest.current_month && (
                    <div style={{ fontSize: '11px', color: '#7f8c8d', borderTop: '1px solid #d4e6f1', paddingTop: '8px' }}>
                      This month (Month {l.live_interest.current_month.month_number}): UGX {Math.round(l.live_interest.current_month.daily_rate).toLocaleString()}/day accruing,
                      day {l.live_interest.current_month.days_elapsed} of 30 — UGX {Math.round(l.live_interest.current_month.accrued_so_far).toLocaleString()} accrued so far this month.
                    </div>
                  )}
                </div>
              )}

              {l.disbursement_ref && (
                <div style={{ marginTop: '10px', fontSize: '12px', background: '#eafaf1', padding: '6px 10px', borderRadius: '4px', color: '#1e8449' }}>
                  💳 Disbursement Reference: <strong>{l.disbursement_ref}</strong>
                </div>
              )}

              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', background: l.status === 'pending' ? '#fef9e7' : '#d5f5e3', color: l.status === 'pending' ? '#b7770d' : '#1e8449' }}>
                  {l.status === 'pending' ? '⏳ Awaiting disbursement' : '✅ Disbursed — ACTIVE'}
                </span>
                <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', background: l.agreement_accepted_at ? '#d5f5e3' : '#fef9e7', color: l.agreement_accepted_at ? '#1e8449' : '#b7770d' }}>
                  {l.agreement_accepted_at ? `✅ Agreement accepted ${fmtTS(l.agreement_accepted_at)}` : '⏳ Agreement not yet accepted'}
                </span>
              </div>

              {(l.status === 'active' || l.status === 'pending') && (
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {!l.agreement_accepted_at && (
                    <>
                      {showAcceptConfirm === l.id ? (
                        <div style={{ background: '#fff9e6', border: '1px solid #f39c12', borderRadius: '6px', padding: '12px 14px', width: '100%' }}>
                          <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '8px', fontSize: '13px' }}>📋 Loan Agreement Acceptance</div>
                          <p style={{ fontSize: '12px', color: '#555', margin: '0 0 10px', lineHeight: '1.6' }}>
                            By clicking <strong>I Accept</strong>, I confirm that I have read and understood the terms of this loan agreement
                            for <strong>UGX {parseFloat(l.principal || 0).toLocaleString()}</strong> ({l.loan_number}),
                            and I agree to repay the full amount plus all applicable charges within the agreed term.
                            This acceptance is legally binding and will be recorded with a timestamp.
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => acceptAgreement(l.id)}
                              disabled={agreementLoading === l.id}
                              style={{ background: agreementLoading === l.id ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', padding: '8px 18px', borderRadius: '4px', cursor: agreementLoading === l.id ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                              {agreementLoading === l.id ? 'Processing...' : '✅ I Accept This Loan Agreement'}
                            </button>
                            <button onClick={() => setShowAcceptConfirm(null)} style={{ background: '#95a5a6', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowAcceptConfirm(l.id)}
                          style={{ background: '#f39c12', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                          📋 Accept Loan Agreement
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'fines' && (
        <div style={cardStyle}>
          <h3 style={sectionTitle}>My Fines</h3>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#2c3e50', color: 'white' }}>
                <th style={thStyle}>Date</th><th style={thStyle}>Type</th><th style={thStyle}>Reason</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {fines.length === 0 && <tr><td colSpan="6" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No fines</td></tr>}
              {fines.map((f, i) => (
                <tr key={f.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                  <td style={tdStyle}>{f.date_issued?.slice(0, 10)}</td>
                  <td style={tdStyle}>{f.fine_type}</td>
                  <td style={tdStyle}>{f.reason}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{parseFloat(f.amount).toLocaleString()}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#e74c3c', fontWeight: 'bold' }}>{parseFloat(f.balance).toLocaleString()}</td>
                  <td style={tdStyle}><span style={{ background: f.status === 'paid' ? '#2ecc71' : '#e74c3c', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{f.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'rosca' && (
        <div>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>ROSCA Registration</h3>
            {!rosca.registrations?.length && <p style={{ color: '#95a5a6' }}>No registrations</p>}
            {rosca.registrations?.map(r => (
              <div key={r.id} style={{ background: '#f9f9f9', borderRadius: '6px', padding: '14px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <strong>{r.cycle_name}</strong>
                  <span style={{ marginLeft: '10px', background: r.cycle_status === 'active' ? '#2ecc71' : '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{r.cycle_status}</span>
                </div>
                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Shares: {r.shares} | Monthly: UGX {parseFloat(r.monthly_contribution).toLocaleString()}</div>
              </div>
            ))}
          </div>
          {rosca.slots?.length > 0 && (
            <div style={{ ...cardStyle, marginTop: '16px' }}>
              <h3 style={sectionTitle}>My Payout Slot</h3>
              {rosca.slots.map(s => (
                <div key={s.id} style={{ background: '#f4ecf7', border: '2px solid #8e44ad', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8e44ad' }}>Slot #{s.slot_number}</div>
                  <div style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '4px' }}>
                    Scheduled: {s.scheduled_date?.slice(0, 10) || `Month ${s.scheduled_month}`} | Payout: UGX {parseFloat(s.payout_amount_primary).toLocaleString()} | Status: <strong>{s.payout_status}</strong>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ ...cardStyle, marginTop: '16px' }}>
            <h3 style={sectionTitle}>ROSCA Contributions</h3>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  <th style={thStyle}>Period</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Due</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Paid</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {!rosca.contributions?.length && <tr><td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No contributions</td></tr>}
                {rosca.contributions?.map((c, i) => (
                  <tr key={c.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={tdStyle}>{MONTHS[c.calendar_month - 1]} {c.calendar_year}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{parseFloat(c.amount_due).toLocaleString()}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#2ecc71' }}>{parseFloat(c.amount_paid).toLocaleString()}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#e74c3c' }}>{parseFloat(c.balance).toLocaleString()}</td>
                    <td style={tdStyle}><span style={{ background: c.status === 'paid' ? '#2ecc71' : c.status === 'partial' ? '#f39c12' : '#e74c3c', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'subscriptions' && (
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Annual Subscriptions</h3>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#2c3e50', color: 'white' }}>
                <th style={thStyle}>Cycle</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Rate</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Paid</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.length === 0 && <tr><td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No subscriptions</td></tr>}
              {subscriptions.map((s, i) => (
                <tr key={s.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                  <td style={tdStyle}>{s.cycle_name || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{parseFloat(s.rate_amount || 0).toLocaleString()}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#2ecc71' }}>{parseFloat(s.amount_paid).toLocaleString()}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#e74c3c' }}>{parseFloat(s.balance).toLocaleString()}</td>
                  <td style={tdStyle}><span style={{ background: s.status === 'paid' ? '#2ecc71' : s.status === 'partial' ? '#f39c12' : '#e74c3c', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{s.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'savings' && (
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Monthly Savings</h3>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#2c3e50', color: 'white' }}>
                <th style={thStyle}>Period</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount Paid</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {savings.length === 0 && <tr><td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No savings records</td></tr>}
              {savings.map((s, i) => (
                <tr key={s.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                  <td style={tdStyle}>{MONTHS[s.month - 1]}{s.cycle_name ? ` — ${s.cycle_name}` : ''}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#2ecc71' }}>{parseFloat(s.amount_paid).toLocaleString()}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#e74c3c' }}>{parseFloat(s.balance).toLocaleString()}</td>
                  <td style={tdStyle}><span style={{ background: s.status === 'paid' ? '#2ecc71' : s.status === 'partial' ? '#f39c12' : '#e74c3c', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{s.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'payments' && (
        <div>
          {adviceLoading && <div style={{ textAlign: 'center', padding: '30px', color: '#95a5a6' }}>Loading payment data...</div>}
          {advice && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <h3 style={{ ...sectionTitle, margin: 0 }}>💳 Payment Advice — {MONTHS[advice.period.month - 1]} {advice.period.year}</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={generateAdvicePDF} style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>⬇ Download PDF</button>
                  <button onClick={loadPaymentsTab} style={{ background: '#ecf0f1', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>↻ Refresh</button>
                </div>
              </div>
              {advice.window && (
                <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '0 0 16px' }}>
                  Valid from {advice.window.start} through {advice.window.end} — includes any outstanding MIFL arrears plus everything payable within this period.
                </p>
              )}
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#2c3e50', color: 'white' }}>
                    <th style={thStyle}>Obligation</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Amount (UGX)</th>
                    <th style={thStyle}>Due Date</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: advice.mifl_arrears.label, amount: advice.mifl_arrears.amount, due_date: null, status: advice.mifl_arrears.amount > 0 ? 'overdue' : 'clear', color: advice.mifl_arrears.amount > 0 ? '#e74c3c' : '#2ecc71', isArrears: advice.mifl_arrears.amount > 0 },
                    ...(advice.obligations || []).map(o => ({ label: o.label || `${o.obligation_type} — Month ${o.month_number}`, amount: parseFloat(o.amount), due_date: o.due_date, status: o.status, color: o.status === 'overdue' ? '#e74c3c' : parseFloat(o.amount) > 0 ? '#e67e22' : '#2ecc71' })),
                    ...(advice.subscription.amount > 0 ? [{ label: advice.subscription.label, amount: advice.subscription.amount, due_date: null, status: advice.subscription.status, color: '#e67e22' }] : []),
                  ].map((row, i) => {
                    const days = daysUntil(row.due_date);
                    const urgency = urgencyInfo(days, row.isArrears === true ? 'arrears' : row.isArrears === false && row.label === advice.mifl_arrears.label ? 'arrears-clear' : row.due_date === null);
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                        <td style={tdStyle}>{row.label}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: row.color }}>{row.amount.toLocaleString()}</td>
                        <td style={tdStyle}>
                          {row.due_date ? (
                            <span>
                              {row.due_date}
                              <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: '600', color: urgency.color }}>({urgency.label})</span>
                            </span>
                          ) : (
                            <span style={{ fontSize: '11px', color: urgency.color, fontWeight: '600' }}>{urgency.label}</span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, fontSize: '11px', color: '#7f8c8d' }}>{row.status}</td>
                      </tr>
                    );
                  })}
                  {customSlipLines.length > 0 && (
                    <>
                      <tr style={{ background: '#f4ecf7' }}>
                        <td colSpan="4" style={{ ...tdStyle, fontWeight: 'bold', color: '#8e44ad', fontSize: '11px', letterSpacing: '0.5px' }}>
                          ADDITIONAL OBLIGATIONS — added by Finance
                        </td>
                      </tr>
                      {customSlipLines.map((line, i) => {
                        const days = daysUntil(line.due_date ? line.due_date.slice(0, 10) : null);
                        const urgency = urgencyInfo(days, !line.due_date);
                        return (
                          <tr key={line.id} style={{ background: i % 2 === 0 ? '#fdf6ff' : 'white' }}>
                            <td style={tdStyle}>{line.description}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#8e44ad' }}>
                              {parseFloat(line.amount).toLocaleString()}
                            </td>
                            <td style={tdStyle}>
                              {line.due_date ? (
                                <span>{line.due_date.slice(0, 10)} <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: '600', color: urgency.color }}>({urgency.label})</span></span>
                              ) : <span style={{ fontSize: '11px', color: '#95a5a6' }}>—</span>}
                            </td>
                            <td style={{ ...tdStyle, fontSize: '11px', color: '#7f8c8d' }}>{line.notes || '—'}</td>
                          </tr>
                        );
                      })}
                    </>
                  )}
                  <tr style={{ background: '#2c3e50', color: 'white' }}>
                    <td style={{ ...tdStyle, fontWeight: 'bold', fontSize: '15px' }}>TOTAL TO DEPOSIT</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '18px' }}>
                      UGX {(advice.total_to_deposit + customSlipLines.reduce((s,l) => s + parseFloat(l.amount), 0)).toLocaleString()}
                    </td>
                    <td style={tdStyle}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <div style={{ ...cardStyle, marginTop: '16px' }}>
            <h3 style={sectionTitle}>📤 Submit Payment Declaration</h3>
            {declMsg && <div style={{ padding: '10px 14px', background: declMsgType === 'error' ? '#fde8e8' : '#d5f5e3', borderLeft: `4px solid ${declMsgType === 'error' ? '#e74c3c' : '#27ae60'}`, borderRadius: '4px', marginBottom: '14px', fontSize: '13px', color: declMsgType === 'error' ? '#c0392b' : '#1e8449' }}>{declMsg}</div>}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={labelStyle}>Amount Declared (UGX) *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="number" style={inputStyle} value={declForm.amount} onChange={e => setDeclForm(f => ({ ...f, amount: e.target.value }))} placeholder="Enter amount deposited" />
                  {advice && advice.total_to_deposit > 0 && (
                    <button onClick={prefillAdviceAmount} style={{ background: '#2c3e50', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}>Use Advice Total</button>
                  )}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={labelStyle}>Deposit Slip (JPG/PNG/PDF)</label>
                <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => setDeclFile(e.target.files[0])} style={{ ...inputStyle, padding: '6px' }} />
              </div>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Notes (optional)</label>
              <input style={inputStyle} value={declForm.notes} onChange={e => setDeclForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Paid at Centenary Bank Kira branch" />
            </div>
            <button onClick={submitDeclaration} disabled={declSubmitting} style={{ background: declSubmitting ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '4px', cursor: declSubmitting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
              {declSubmitting ? 'Submitting...' : '✓ Submit Declaration'}
            </button>
          </div>
          {declarations.length > 0 && (
            <div style={{ ...cardStyle, marginTop: '16px' }}>
              <h3 style={sectionTitle}>📋 My Payment Declarations</h3>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#2c3e50', color: 'white' }}>
                    <th style={thStyle}>Date</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                    <th style={thStyle}>Slip</th><th style={thStyle}>Notes</th>
                    <th style={thStyle}>Status</th><th style={thStyle}>Confirmed By</th>
                  </tr>
                </thead>
                <tbody>
                  {declarations.map((d, i) => (
                    <tr key={d.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={tdStyle}>{d.declaration_date?.slice(0, 10)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>UGX {parseFloat(d.amount).toLocaleString()}</td>
                      <td style={tdStyle}>{d.deposit_slip_path ? <a href={`/api/portal/deposit-slip/${d.deposit_slip_path}`} target="_blank" rel="noreferrer" style={{ color: '#2980b9', fontSize: '12px' }}>View Slip</a> : <span style={{ color: '#95a5a6', fontSize: '12px' }}>No slip</span>}</td>
                      <td style={{ ...tdStyle, fontSize: '12px' }}>{d.notes || '—'}</td>
                      <td style={tdStyle}><span style={{ background: d.status === 'confirmed' ? '#2ecc71' : d.status === 'rejected' ? '#e74c3c' : '#f39c12', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{d.status.toUpperCase()}</span></td>
                      <td style={{ ...tdStyle, fontSize: '12px' }}>{d.confirmed_by_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'loan_apply' && (
        <div>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>📝 Apply for a Loan</h3>
            {loanMsg && <div style={{ padding: '10px 14px', background: loanMsgType === 'error' ? '#fde8e8' : '#d5f5e3', borderLeft: `4px solid ${loanMsgType === 'error' ? '#e74c3c' : '#27ae60'}`, borderRadius: '4px', marginBottom: '14px', fontSize: '13px', color: loanMsgType === 'error' ? '#c0392b' : '#1e8449' }}>{loanMsg}</div>}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={labelStyle}>Loan Type *</label>
                <select style={inputStyle} value={loanForm.loan_type} onChange={e => setLoanForm(f => ({ ...f, loan_type: e.target.value }))}>
                  {LOAN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={labelStyle}>Amount Requested (UGX) *</label>
                <input type="number" style={inputStyle} value={loanForm.amount_requested} onChange={e => setLoanForm(f => ({ ...f, amount_requested: e.target.value }))} placeholder="e.g. 500000" />
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Purpose / Reason *</label>
              <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }} value={loanForm.purpose} onChange={e => setLoanForm(f => ({ ...f, purpose: e.target.value }))} placeholder="Describe the purpose of this loan" />
            </div>
            <button onClick={submitLoanApplication} disabled={loanSubmitting} style={{ background: loanSubmitting ? '#95a5a6' : '#2c3e50', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '4px', cursor: loanSubmitting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
              {loanSubmitting ? 'Submitting...' : '📨 Submit Application'}
            </button>
          </div>
          {loanApps.length > 0 && (
            <div style={{ ...cardStyle, marginTop: '16px' }}>
              <h3 style={sectionTitle}>My Loan Applications</h3>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#2c3e50', color: 'white' }}>
                    <th style={thStyle}>Date</th><th style={thStyle}>Type</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Amount Requested</th>
                    <th style={thStyle}>Purpose</th><th style={thStyle}>Status</th><th style={thStyle}>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {loanApps.map((a, i) => (
                    <tr key={a.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={tdStyle}>{a.applied_at?.slice(0, 10)}</td>
                      <td style={tdStyle}>{LOAN_TYPES.find(t => t.value === a.loan_type)?.label || a.loan_type}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>UGX {parseFloat(a.amount_requested).toLocaleString()}</td>
                      <td style={{ ...tdStyle, fontSize: '12px' }}>{a.purpose || '—'}</td>
                      <td style={tdStyle}><span style={{ background: a.status === 'approved' ? '#2ecc71' : a.status === 'rejected' ? '#e74c3c' : '#f39c12', color: 'white', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>{a.status.toUpperCase()}</span></td>
                      <td style={{ ...tdStyle, fontSize: '12px' }}>
                        {a.approved_amount && <div style={{ color: '#27ae60', fontWeight: 'bold' }}>✅ UGX {parseFloat(a.approved_amount).toLocaleString()}</div>}
                        {a.loan_id && <div style={{ color: '#1a5276', fontSize: '11px' }}>→ Go to Loans tab to accept agreement</div>}
                        {a.admin_notes && <div style={{ color: '#2980b9' }}>{a.admin_notes}</div>}
                        {a.rejection_reason && <div style={{ color: '#e74c3c' }}>{a.rejection_reason}</div>}
                        {!a.approved_amount && !a.admin_notes && !a.rejection_reason && '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'finance' && financialPos && (
        <div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {[
              { label: 'Treasury Cash',     val: parseFloat(financialPos.treasury_cash).toLocaleString(),     color: '#27ae60' },
              { label: 'Loan Receivables',  val: parseFloat(financialPos.loan_receivables).toLocaleString(),  color: '#2980b9' },
              { label: 'True Net Position', val: parseFloat(financialPos.true_net_position).toLocaleString(), color: '#2c3e50' },
            ].map((c, i) => (
              <div key={i} style={{ background: c.color, color: 'white', padding: '16px 20px', borderRadius: '8px', flex: '1', minWidth: '160px' }}>
                <div style={{ fontSize: '11px', opacity: 0.85 }}>{c.label}</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '4px' }}>UGX {c.val}</div>
              </div>
            ))}
          </div>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Account Balances</h3>
            <table style={tableStyle}>
              <thead><tr style={{ background: '#2c3e50', color: 'white' }}><th style={thStyle}>Account</th><th style={{ ...thStyle, textAlign: 'right' }}>Balance (UGX)</th></tr></thead>
              <tbody>
                {financialPos.accounts?.map((a, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={tdStyle}>{a.account_name}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#27ae60' }}>{parseFloat(a.balance).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'treasury' && (
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Treasury Accounts</h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {treasuryAccounts.map((a, i) => (
              <div key={i} style={{ background: '#2c3e50', color: 'white', padding: '16px 20px', borderRadius: '8px', flex: '1', minWidth: '160px' }}>
                <div style={{ fontSize: '11px', opacity: 0.85 }}>{a.account_name}</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '4px' }}>UGX {parseFloat(a.balance).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div style={{ background: '#f9f9f9', borderRadius: '6px', padding: '12px 16px', fontSize: '12px', color: '#7f8c8d' }}>
            ℹ Treasury balances are read-only. Transactions are managed by Finance.
          </div>
        </div>
      )}

      {tab === 'projections' && (
        <div>
          {!projections ? (
            <div style={{ ...cardStyle, textAlign: 'center', color: '#95a5a6' }}>No approved budget yet</div>
          ) : (
            <>
              <div style={{ background: '#eafaf1', border: '1px solid #2ecc71', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px' }}>
                <strong>{projections.budget.cycle_name}</strong> — {projections.budget.financial_year} —
                <span style={{ background: '#2ecc71', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', marginLeft: '8px' }}>{projections.budget.status.toUpperCase()}</span>
              </div>
              <div style={cardStyle}>
                <h3 style={{ ...sectionTitle, color: '#27ae60' }}>📈 Income</h3>
                <table style={tableStyle}>
                  <thead><tr style={{ background: '#27ae60', color: 'white' }}><th style={thStyle}>Category</th><th style={{ ...thStyle, textAlign: 'right' }}>Projected</th><th style={{ ...thStyle, textAlign: 'right' }}>Actual</th><th style={{ ...thStyle, textAlign: 'right' }}>Variance</th></tr></thead>
                  <tbody>
                    {projections.lines.filter(l => l.line_type === 'income').map((l, i) => {
                      const actual = projections.actuals?.income?.[l.category] || 0;
                      const diff = actual - parseFloat(l.projected_amount);
                      return (
                        <tr key={l.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                          <td style={tdStyle}>{l.category}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{parseFloat(l.projected_amount).toLocaleString()}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{actual.toLocaleString()}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: diff >= 0 ? '#2ecc71' : '#e74c3c', fontWeight: 'bold' }}>{diff >= 0 ? '+' : ''}{diff.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'arrears' && (
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Members in Arrears</h3>
          <p style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '12px' }}>Per constitution — members with outstanding MIFL balances are visible to all members.</p>
          <table style={tableStyle}>
            <thead><tr style={{ background: '#e74c3c', color: 'white' }}><th style={thStyle}>Member</th><th style={thStyle}>Number</th><th style={{ ...thStyle, textAlign: 'right' }}>MIFL Balance (UGX)</th></tr></thead>
            <tbody>
              {arrears.length === 0 && <tr><td colSpan="3" style={{ ...tdStyle, textAlign: 'center', color: '#2ecc71' }}>✓ No members in arrears</td></tr>}
              {arrears.map((a, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff5f5' : 'white' }}>
                  <td style={tdStyle}><strong>{a.full_name}</strong></td>
                  <td style={tdStyle}>{a.member_number}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#e74c3c', fontWeight: 'bold' }}>{parseFloat(a.mifl_balance).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'bio' && (
        <div style={cardStyle}>
          <h3 style={sectionTitle}>👤 My Family & BIO Information</h3>
          <p style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '16px' }}>
            This information is maintained by the Secretariat. Contact the Secretary to update any details.
          </p>
          {!bioPData ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>👤</div>
              No BIO data on file yet — contact the Secretariat to have your information recorded.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '250px' }}>
                <div style={{ background: '#f9f9f9', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                  <div style={{ fontWeight: 'bold', color: '#1a5276', marginBottom: '12px', fontSize: '13px' }}>👨‍👩‍👧 Parents</div>
                  {bioPData.father_name ? (
                    <div style={{ marginBottom: '10px', fontSize: '13px' }}>
                      <span style={{ color: '#7f8c8d', fontSize: '11px', display: 'block', marginBottom: '2px' }}>Father</span>
                      <span style={{ fontWeight: '600' }}>{bioPData.father_name}</span>
                      {bioPData.father_deceased && <span style={{ marginLeft: '8px', background: '#fadbd8', color: '#c0392b', padding: '1px 6px', borderRadius: '4px', fontSize: '11px' }}>✝ Deceased</span>}
                    </div>
                  ) : <div style={{ color: '#95a5a6', fontSize: '12px', marginBottom: '10px' }}>Father — not recorded</div>}
                  {bioPData.mother_name ? (
                    <div style={{ fontSize: '13px' }}>
                      <span style={{ color: '#7f8c8d', fontSize: '11px', display: 'block', marginBottom: '2px' }}>Mother</span>
                      <span style={{ fontWeight: '600' }}>{bioPData.mother_name}</span>
                      {bioPData.mother_deceased && <span style={{ marginLeft: '8px', background: '#fadbd8', color: '#c0392b', padding: '1px 6px', borderRadius: '4px', fontSize: '11px' }}>✝ Deceased</span>}
                    </div>
                  ) : <div style={{ color: '#95a5a6', fontSize: '12px' }}>Mother — not recorded</div>}
                </div>
                <div style={{ background: '#f9f9f9', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                  <div style={{ fontWeight: 'bold', color: '#1a5276', marginBottom: '8px', fontSize: '13px' }}>💍 Spouse</div>
                  {bioPData.spouse_name ? <span style={{ fontWeight: '600', fontSize: '13px' }}>{bioPData.spouse_name}</span> : <span style={{ color: '#95a5a6', fontSize: '12px' }}>Not recorded</span>}
                </div>
                <div style={{ background: '#f9f9f9', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ fontWeight: 'bold', color: '#1a5276', marginBottom: '8px', fontSize: '13px' }}>🆘 Next of Kin</div>
                  {bioPData.next_of_kin_name ? (
                    <div style={{ fontSize: '13px' }}>
                      <div style={{ fontWeight: '600' }}>{bioPData.next_of_kin_name}</div>
                      {bioPData.next_of_kin_relationship && <div style={{ color: '#7f8c8d', fontSize: '12px', marginTop: '2px' }}>{bioPData.next_of_kin_relationship}</div>}
                    </div>
                  ) : <span style={{ color: '#95a5a6', fontSize: '12px' }}>Not recorded</span>}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: '220px' }}>
                <div style={{ background: '#f9f9f9', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ fontWeight: 'bold', color: '#1a5276', marginBottom: '12px', fontSize: '13px' }}>👶 Children ({bioChildrenP.length})</div>
                  {bioChildrenP.length === 0
                    ? <span style={{ color: '#95a5a6', fontSize: '12px' }}>No children recorded</span>
                    : bioChildrenP.map((c, i) => (
                        <div key={c.id} style={{ padding: '8px 12px', background: i % 2 === 0 ? 'white' : '#f0f0f0', borderRadius: '4px', marginBottom: '4px', fontSize: '13px' }}>👤 {c.child_name}</div>
                      ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

const cardStyle    = { background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '16px' };
const sectionTitle = { margin: '0 0 16px', color: '#2c3e50', fontSize: '15px', fontWeight: 'bold' };
const tableStyle   = { width: '100%', borderCollapse: 'collapse' };
const thStyle      = { padding: '9px', textAlign: 'left', fontSize: '12px' };
const tdStyle      = { padding: '9px', borderBottom: '1px solid #eee', fontSize: '13px' };
const inputStyle   = { width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' };
const labelStyle   = { display: 'block', fontSize: '12px', fontWeight: '600', color: '#2c3e50', marginBottom: '4px' };

export default MemberPortal;